import React from "react"
import { Badge } from "../../components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { 
  Shield, 
  FileText, 
  Cookie,
  Eye,
  Lock,
  Users,
  Globe,
  Mail,
  Calendar
} from "lucide-react"
import SEO from "../SEO"

export default function PrivacyPolicy() {
  const privacyPolicySchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Privacy Policy - Pranaam Hospitals",
    "description": "Pranaam Hospitals Privacy Policy outlining how we collect, use, and protect your personal information.",
    "url": "https://pranaam.pranaamhospitals.in/patient-care/privacy-policy"
  }

  return (
    <>
      <SEO
        title="Privacy Policy | Pranaam Hospitals - Data Protection & Privacy Rights"
        description="Learn about Pranaam Hospitals' Privacy Policy, how we collect, use, and protect your personal information and your privacy rights."
        canonical="https://pranaam.pranaamhospitals.in/patient-care/privacy-policy"
        schema={privacyPolicySchema}
      />

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-primary/10 to-primary/5 py-16">
          <div className="container px-4 mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 px-3 py-1">Patient Care</Badge>
              <h1 className="mb-6 text-4xl font-bold tracking-tight md:text-5xl">
                Privacy <span className="text-primary">Policy</span>
              </h1>
              <div className="flex items-center justify-center gap-2 mb-6">
                <Calendar className="w-5 h-5 text-primary" />
                <p className="text-lg text-gray-700">Last updated: February 23, 2022</p>
              </div>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information when You use the Service and tells You about Your privacy rights and how the law protects You.
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12">
          <div className="container px-4 mx-auto">
            <div className="max-w-6xl mx-auto space-y-8">
              
              {/* Introduction */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-6 h-6 text-primary" />
                    Introduction
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">
                    We use Your Personal data to provide and improve the Service. By using the Service, You agree to the collection and use of information in accordance with this Privacy Policy.
                  </p>
                </CardContent>
              </Card>

              {/* Interpretation */}
              <Card>
                <CardHeader>
                  <CardTitle>Interpretation</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">
                    The words of which the initial letter is capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.
                  </p>
                </CardContent>
              </Card>

              {/* Definitions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-6 h-6 text-primary" />
                    Definitions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <h4 className="font-semibold text-blue-800 mb-2">Account</h4>
                        <p className="text-blue-700 text-sm">A unique account created for You to access our Service or parts of our Service.</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg">
                        <h4 className="font-semibold text-green-800 mb-2">Company</h4>
                        <p className="text-green-700 text-sm">Refers to Pranaam Hospitals Pvt Ltd, 1-56/6/40& 41, Pranaam Hospital Ln, Mythri Nagar, Madeenaguda, Telangana 500050.</p>
                      </div>
                      <div className="p-4 bg-orange-50 rounded-lg">
                        <h4 className="font-semibold text-orange-800 mb-2">Cookies</h4>
                        <p className="text-orange-700 text-sm">Small files placed on Your device containing details of Your browsing history.</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <h4 className="font-semibold text-purple-800 mb-2">Personal Data</h4>
                        <p className="text-purple-700 text-sm">Any information that relates to an identified or identifiable individual.</p>
                      </div>
                      <div className="p-4 bg-red-50 rounded-lg">
                        <h4 className="font-semibold text-red-800 mb-2">Service</h4>
                        <p className="text-red-700 text-sm">Refers to the Website.</p>
                      </div>
                      <div className="p-4 bg-indigo-50 rounded-lg">
                        <h4 className="font-semibold text-indigo-800 mb-2">Website</h4>
                        <p className="text-indigo-700 text-sm">Refers to Pranaam Hospitals, accessible from www.pranaaamhospitals.com</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Collecting Personal Data */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-6 h-6 text-primary" />
                    Collecting and Using Your Personal Data
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Personal Data</h3>
                    <p className="text-gray-700 mb-4">
                      While using Our Service, We may ask You to provide Us with certain personally identifiable information that can be used to contact or identify You. Personally identifiable information may include, but is not limited to:
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li>• Email address</li>
                      <li>• First name and last name</li>
                      <li>• Address, State, Province, ZIP/Postal code, City</li>
                      <li>• Usage Data</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Usage Data</h3>
                    <p className="text-gray-700 mb-4">
                      Usage Data is collected automatically when using the Service. Usage Data may include information such as Your Device's Internet Protocol address (e.g. IP address), browser type, browser version, the pages of our Service that You visit, the time and date of Your visit, the time spent on those pages, unique device identifiers and other diagnostic data.
                    </p>
                    <p className="text-gray-700">
                      When You access the Service by or through a mobile device, We may collect certain information automatically, including the type of mobile device You use, Your mobile device unique ID, the IP address of Your mobile device, Your mobile operating system, and other diagnostic data.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Tracking Technologies and Cookies */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Cookie className="w-6 h-6 text-primary" />
                    Tracking Technologies and Cookies
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-gray-700">
                    We use Cookies and similar tracking technologies to track the activity on Our Service and store certain information. The technologies We use may include:
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
                      <h4 className="font-semibold text-yellow-800 mb-2">Necessary / Essential Cookies</h4>
                      <p className="text-yellow-700 text-sm mb-2"><strong>Type:</strong> Session Cookies</p>
                      <p className="text-yellow-700 text-sm">Essential to provide services and authenticate users.</p>
                    </div>
                    
                    <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                      <h4 className="font-semibold text-blue-800 mb-2">Cookies Policy Acceptance</h4>
                      <p className="text-blue-700 text-sm mb-2"><strong>Type:</strong> Persistent Cookies</p>
                      <p className="text-blue-700 text-sm">Identify if users have accepted the use of cookies.</p>
                    </div>
                    
                    <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
                      <h4 className="font-semibold text-green-800 mb-2">Functionality Cookies</h4>
                      <p className="text-green-700 text-sm mb-2"><strong>Type:</strong> Persistent Cookies</p>
                      <p className="text-green-700 text-sm">Remember your choices and preferences.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Use of Personal Data */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-6 h-6 text-primary" />
                    Use of Your Personal Data
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4">The Company may use Personal Data for the following purposes:</p>
                  <ul className="space-y-3 text-gray-700">
                    <li>• <strong>To provide and maintain our Service:</strong> Including to monitor the usage of our Service</li>
                    <li>• <strong>To manage Your Account:</strong> To manage Your registration as a user of the Service</li>
                    <li>• <strong>For the performance of a contract:</strong> Development, compliance and undertaking of purchase contracts</li>
                    <li>• <strong>To contact You:</strong> By email, telephone calls, SMS, or other forms of electronic communication</li>
                    <li>• <strong>To provide You with news:</strong> Special offers and general information about similar goods and services</li>
                    <li>• <strong>To manage Your requests:</strong> To attend and manage Your requests to Us</li>
                    <li>• <strong>For business transfers:</strong> To evaluate or conduct mergers, acquisitions, or asset transfers</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Data Sharing */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-6 h-6 text-primary" />
                    Sharing Your Personal Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4">We may share Your personal information in the following situations:</p>
                  <ul className="space-y-2 text-gray-700">
                    <li>• <strong>With Service Providers:</strong> To monitor and analyze the use of our Service</li>
                    <li>• <strong>For business transfers:</strong> During mergers, acquisitions, or asset sales</li>
                    <li>• <strong>With Affiliates:</strong> Including parent company and subsidiaries</li>
                    <li>• <strong>With business partners:</strong> To offer certain products, services or promotions</li>
                    <li>• <strong>With other users:</strong> When You share information in public areas</li>
                    <li>• <strong>With Your consent:</strong> For any other purpose with Your consent</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Data Security */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="w-6 h-6 text-primary" />
                    Security of Your Personal Data
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">
                    The security of Your Personal Data is important to Us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure. While We strive to use commercially acceptable means to protect Your Personal Data, We cannot guarantee its absolute security.
                  </p>
                </CardContent>
              </Card>

              {/* Children's Privacy */}
              <Card>
                <CardHeader>
                  <CardTitle>Children's Privacy</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">
                    Our Service does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from anyone under the age of 13. If You are a parent or guardian and You are aware that Your child has provided Us with Personal Data, please contact Us.
                  </p>
                </CardContent>
              </Card>

              {/* Data Retention */}
              <Card>
                <CardHeader>
                  <CardTitle>Retention of Your Personal Data</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">
                    The Company will retain Your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use Your Personal Data to comply with our legal obligations, resolve disputes, and enforce our legal agreements and policies.
                  </p>
                </CardContent>
              </Card>

              {/* Changes to Policy */}
              <Card>
                <CardHeader>
                  <CardTitle>Changes to this Privacy Policy</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">
                    We may update Our Privacy Policy from time to time. We will notify You of any changes by posting the new Privacy Policy on this page and will let You know via email and/or a prominent notice on Our Service, prior to the change becoming effective.
                  </p>
                </CardContent>
              </Card>

              {/* Contact Information */}
              <Card className="border-l-4 border-primary">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-primary">
                    <Mail className="w-6 h-6" />
                    Contact Us
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4">
                    If you have any questions about this Privacy Policy, You can contact us:
                  </p>
                  <p className="text-gray-700">
                    By visiting this page on our website: <a href="https://pranaamhospitals.com/privacy-policy/" className="text-primary hover:underline">https://pranaamhospitals.com/privacy-policy/</a>
                  </p>
                  <p className="text-sm text-gray-600 mt-4">
                    This Privacy Policy has been created with the help of the Privacy Policy Template.
                  </p>
                </CardContent>
              </Card>

            </div>
          </div>
        </section>
      </div>
    </>
  )
}